import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import { useTranslation } from 'next-i18next'
import React from 'react'
import { useClickIdsForPrefill } from 'src/hooks/useClickIdsForPrefill'
import { useWaSessionToken } from 'src/hooks/useWaSessionToken'
import { ILocation } from 'src/types/graphql'
import ga from 'src/utils/pixels/gtag'
import { aliasWaSession } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

type IProps = {
  location?: Pick<ILocation, 'urlRoute'>
}

function ContactUs({ location }: IProps) {
  const { t } = useTranslation('details')
  // Both mount-gated so the SSR'd href matches first client paint (#418).
  // The server reads `sid` and the click IDs back out of the listing URL in
  // the opening message — without them this button's leads never reached
  // Google Ads (2026-10-06 review). Same carriers as WrapperWA.
  const waSessionToken = useWaSessionToken()
  const clickIds = useClickIdsForPrefill()

  const params = new URLSearchParams()
  if (waSessionToken) params.set('sid', waSessionToken)
  if (clickIds.gclid) params.set('gclid', clickIds.gclid)
  if (clickIds.fbclid) params.set('fbclid', clickIds.fbclid)
  if (clickIds.msclkid) params.set('msclkid', clickIds.msclkid)
  const query = params.toString()
  const route = location?.urlRoute
  const separator = route?.includes('?') ? '&' : '?'
  const url = route
    ? `https://www.spotme.mx${route}${query ? `${separator}${query}` : ''}`
    : 'https://www.spotme.mx'
  const whatsappUrl = buildWhatsappUrl(t('CONTACT_US.WHATSAPP_MSG', { url }))

  const handleClick = () => {
    // Alias only when the `sid` actually rides the prefill (waSessionToken.ts
    // rule 1) — the bare-homepage fallback URL carries no token.
    if (route) aliasWaSession(waSessionToken)
    ga.event('listing_help_wa_click', {
      has_session_token: !!waSessionToken,
      has_gclid: !!clickIds.gclid
    })
  }

  return (
    <section className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12">
      <div className="md:col-span-7">
        <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
          {t('SECTIONS.ADVICE_EYEBROW')}
        </p>
        <h2 className="mt-3 max-w-2xl font-display text-[28px] leading-[1.1] font-medium text-ink-700 md:text-[44px]">
          {`${t('SECTIONS.HELP_TITLE_LEAD')} `}
          <span className="font-display text-blue-500 italic">
            {t('SECTIONS.HELP_TITLE_ACCENT')}
          </span>
          ?
        </h2>
        <p className="mt-5 max-w-lg text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
          {t('SECTIONS.HELP_SUBTITLE')}
        </p>
      </div>

      <div className="flex flex-col items-start gap-3 md:col-span-5 md:items-end md:self-end">
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="inline-flex items-center gap-2 border border-ink-700 bg-ink-700 px-7 py-3.5 text-[12px] font-medium tracking-[0.14em] text-white-500 uppercase transition-colors hover:border-blue-500 hover:bg-blue-500"
        >
          <WhatsAppIcon className="!text-[18px]" />
          Habla con un experto
        </a>
        <span className="font-display text-[11px] tracking-[0.16em] text-ink-200 uppercase">
          Respuesta en menos de 1 hora
        </span>
      </div>
    </section>
  )
}

export default React.memo(ContactUs)
